import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { PopoverContent } from "@/components/ui/popover";
import { ShiftPlannerTimes } from "./shiftPlanner.types";
import { grossHours, nextBreakHours, prevBreakHours, snapBreakHours } from "./shiftPlanner.utils";

/** The standard-break rule (same as the legacy popover and the backend's
 * reported-time default): 0.5 h for shifts of 5 hours or more, else 0. */
function withDefaultBreak(times: ShiftPlannerTimes): ShiftPlannerTimes {
	return { ...times, breakHours: grossHours(times) >= 5 ? 0.5 : 0 };
}

export interface ShiftEditPopoverProps {
	/** Shown with an "Ändra" link when every edited slot belongs to the same consultant. */
	consultantName?: string;
	/** Number of slots being edited — shown when the selection spans several slots. */
	slotCount?: number;
	/** Initial values for the draft. */
	times: ShiftPlannerTimes;
	/** Show "Antal platser": the session only CREATES unassigned shifts
	 * (header slots / empty demand cells), so each slot may create several
	 * parallel shifts. */
	askCount?: boolean;
	/** Set on demand-creating sessions: renders the "Lägg till behov" header
	 * with this target summary ("Tis 18 aug" / "N dagar") instead of the
	 * consultant/slot-count header. */
	createSubtitle?: string;
	/** Overrides the submit button label ("Lägg till" for create sessions;
	 * default "Klar"). */
	submitLabel?: string;
	/** Commit the draft — Enter or the "Klar" button. `count` parallel shifts
	 * per creation slot (1 unless `askCount`). */
	onDone: (times: ShiftPlannerTimes, count: number) => void;
	/** Close without committing — Esc, outside click or "Stäng". */
	onCancel: () => void;
	/**
	 * "Ändra" clicked (change the consultant of a booked shift) — the draft is
	 * passed along so the caller can commit it before opening the picker.
	 * Unassigned shifts have no picker link here: consultants are chosen via
	 * the "Välj konsult" row link instead.
	 */
	onRequestConsultantPicker?: (times: ShiftPlannerTimes) => void;
	/**
	 * Seed the draft with the standard break when `times` has none. True when
	 * CREATING shifts; existing shifts must open showing their stored values,
	 * or "Klar"/"Ändra" would commit a break the user never chose. Time edits
	 * still re-suggest the break either way (until the break field is touched).
	 */
	suggestBreakOnOpen?: boolean;
}

/**
 * The Från-Till/Rast editor rendered inside a Radix popover anchored at a
 * ShiftPlanner cell or day header. Edits are held in a local draft and only
 * committed on Enter / "Klar" — cancelling leaves the grid untouched.
 */
const ShiftEditPopover: React.FC<ShiftEditPopoverProps> = ({
	consultantName,
	slotCount = 1,
	times,
	askCount = false,
	createSubtitle,
	submitLabel,
	onDone,
	onCancel,
	onRequestConsultantPicker,
	suggestBreakOnOpen = true,
}) => {
	const { t } = useTranslation();

	const startInputRef = useRef<HTMLInputElement>(null);
	const detachBreakWheel = useRef<() => void>();
	const [count, setCount] = useState(1);

	// New drafts without an explicit break get the standard break suggested, and
	// it follows time edits until the user touches the break field themselves.
	const [draft, setDraft] = useState<ShiftPlannerTimes>(() =>
		times.breakHours !== undefined || !suggestBreakOnOpen ? times : withDefaultBreak(times)
	);
	const breakTouched = useRef(times.breakHours !== undefined);

	function setTime(field: "startTime" | "endTime", value: string) {
		if (!value) return;
		const next = { ...draft, [field]: value };
		setDraft(breakTouched.current ? next : withDefaultBreak(next));
	}

	function setBreak(value: string) {
		breakTouched.current = true;
		const parsed = parseFloat(value.replace(",", "."));
		setDraft({ ...draft, breakHours: isNaN(parsed) ? undefined : Math.max(0, parsed) });
	}

	/**
	 * Move one rung along the break ladder (0, 0.5, 0.75, 1, 1.5 ...). Every way
	 * of stepping the field routes through here, because the native step cannot
	 * express the lone 0.75 rung. Uses the updater form so the wheel listener
	 * below never has to re-subscribe to read the current draft.
	 */
	function stepBreak(direction: "up" | "down") {
		breakTouched.current = true;
		setDraft((prev) => {
			const current = prev.breakHours ?? 0;
			return {
				...prev,
				breakHours: direction === "up" ? nextBreakHours(current) : prevBreakHours(current),
			};
		});
	}

	/**
	 * The field is `step="any"` so typing 0.75 does not trip step-mismatch
	 * validation and block the submit — off-ladder typing is snapped instead, on
	 * blur and on commit. That leaves nothing for the browser to step by, so the
	 * arrow keys are driven here.
	 */
	function onBreakKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
		e.preventDefault();
		stepBreak(e.key === "ArrowUp" ? "up" : "down");
	}

	/**
	 * ...and so is the scroll wheel, which `step="any"` otherwise leaves stepping
	 * by the browser's fallback of 1 whole hour.
	 *
	 * A native listener, not React's `onWheel`: React registers wheel listeners
	 * passively at the root, where `preventDefault` is a no-op — and without it
	 * the surface behind the popover scrolls as well. It is attached from a
	 * callback ref rather than an effect because Radix mounts the popover
	 * content after this component's own effects run, so a mount-time ref is
	 * still null. Chrome only wheel-steps a *focused* number field; match that
	 * rather than hijacking the wheel from anyone merely scrolling past.
	 */
	const attachBreakWheel = useCallback((input: HTMLInputElement | null) => {
		detachBreakWheel.current?.();
		detachBreakWheel.current = undefined;
		if (!input) return;

		function onWheel(e: WheelEvent) {
			if (!e.deltaY || document.activeElement !== input) return;
			e.preventDefault();
			stepBreak(e.deltaY < 0 ? "up" : "down");
		}

		input.addEventListener("wheel", onWheel, { passive: false });
		detachBreakWheel.current = () => input.removeEventListener("wheel", onWheel);
		// stepBreak only touches a setState updater and a ref, so it is stable
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Only a break the user actually entered is snapped: an existing shift may
	 * carry an off-ladder break from elsewhere (a time report, a customer
	 * default), and committing an untouched popover must not silently rewrite it.
	 */
	function snappedDraft(): ShiftPlannerTimes {
		return !breakTouched.current || draft.breakHours === undefined
			? draft
			: { ...draft, breakHours: snapBreakHours(draft.breakHours) };
	}

	return (
		<PopoverContent
			align="start"
			className="w-80"
			onOpenAutoFocus={(e) => {
				// Radix would focus the popover container itself — put the user
				// straight into the start-time input instead.
				e.preventDefault();
				startInputRef.current?.focus();
			}}
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					// React propagates events through the COMPONENT tree, portal or
					// not — without this, Enter here also submits a host page's form
					// (e.g. the job create form wrapping the planner)
					e.stopPropagation();
					onDone(snappedDraft(), askCount ? count : 1);
				}}
				className="flex flex-col gap-3"
			>
				{createSubtitle ? (
					// Creating demand — the same header wherever the create starts
					// (day-header (+), empty demand cells, header selections)
					<div className="border-b border-gray-200 pb-2">
						<div className="text-sm font-semibold">{t("shiftPlanner.addDemand")}</div>
						<div className="text-xs text-gray-500">{createSubtitle}</div>
					</div>
				) : (
					(consultantName || slotCount > 1) && (
						<div className="flex items-center justify-between border-b border-gray-200 pb-2">
							{!consultantName ? (
								// Unassigned or mixed selection — just say how many slots
								<span className="text-sm font-semibold">
									{t("shiftPlanner.selectedShifts", { count: slotCount })}
								</span>
							) : (
								<>
									<span className="text-sm font-semibold">{consultantName}</span>
									{onRequestConsultantPicker && (
										<button
											type="button"
											onClick={() => onRequestConsultantPicker(snappedDraft())}
											className="text-sm text-action underline hover:no-underline"
										>
											{t("shiftPlanner.change")}
										</button>
									)}
								</>
							)}
						</div>
					)
				)}

				<div>
					<div className="mb-1 text-sm font-semibold">{t("shiftPlanner.fromTo")}</div>
					<div className="flex items-center gap-2">
						<Input
							ref={startInputRef}
							type="time"
							value={draft.startTime}
							onChange={(e) => setTime("startTime", e.target.value)}
							className="w-28"
						/>
						<Input
							type="time"
							value={draft.endTime}
							onChange={(e) => setTime("endTime", e.target.value)}
							className="w-28"
						/>
					</div>
				</div>

				<div className="flex items-start gap-4">
					<div>
						<div className="mb-1 text-sm font-semibold">{t("shiftPlanner.breakHours")}</div>
						<div className="flex items-start gap-3">
							<Input
								ref={attachBreakWheel}
								type="number"
								min={0}
								step="any"
								value={draft.breakHours ?? ""}
								onChange={(e) => setBreak(e.target.value)}
								onKeyDown={onBreakKeyDown}
								onBlur={() => setDraft(snappedDraft())}
								className="w-16"
							/>
							{!askCount && (
								<p className="text-xs text-muted-foreground">{t("shiftPlanner.breakHint")}</p>
							)}
						</div>
					</div>
					{askCount && (
						<div>
							<div className="mb-1 text-sm font-semibold">{t("shiftPlanner.demandCount")}</div>
							<Input
								type="number"
								min={1}
								max={99}
								value={count}
								// Typing a value should just replace the old one
								onFocus={(e) => e.target.select()}
								onChange={(e) => setCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
								className="w-16"
							/>
						</div>
					)}
				</div>

				<div className="flex items-center justify-between pt-1">
					<button
						type="button"
						onClick={onCancel}
						className="text-sm text-gray-600 underline hover:no-underline"
					>
						{t("shiftPlanner.close")}
					</button>
					<button
						type="submit"
						className="rounded-md bg-success px-4 py-1.5 text-sm font-semibold text-white hover:opacity-90"
					>
						{submitLabel ?? (t("shiftPlanner.done") as string)}
					</button>
				</div>
			</form>
		</PopoverContent>
	);
};

export default ShiftEditPopover;
